import { CheckDWSIncome } from '@/apis/family_application/dws'
import { FilingStatusSchema } from '@/apis/family_application/requests'
import { LogUserActivity } from '@/apis/users/requests'
import { useAppAlert } from '@/app/AppAlertProvider'
import AppAlertComponent from '@/app/common/components/AppAlertComponent/AppAlertComponent'
import { Button, buttonVariants } from '@/app/common/components/Button'
import ContactSupportModal from '@/app/common/components/ContactSupportModal/ContactSupportModal'
import { DatePicker } from '@/app/common/components/DatePicker'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import LoadingCard from '@/app/common/components/LoadingCard'
import SSNInput, {
  isMaskedSSN,
  isValidSSN,
  MASKED_SSN_STRUCTURE,
} from '@/app/common/components/SSNInput'
import { static_errors } from '@/app/common/constants'
import { isV1RenewalApplication } from '@/app/utils/student/student.utils'
import { FamilyApplicationData } from '@/layouts/FamilyApplicationLayout'
import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import { UpdateApplicant, UpdateIncomeDetails } from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch, RootState } from '@/storage/store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { format, Locale, parse } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useOutletContext } from 'react-router-dom'
import { z } from 'zod'
import { isRenewalApplicant, LOG_TYPES } from '../../constants'
import FamilyWizardLayout from '../../FamilyWizardLayout'
import { SubmitButton } from '../../student_details/shared/submit-button'

export const SSNSchema = z.string().refine((val) => isValidSSN(val), {
  message: 'invalid_ssn',
})

export const ssnFormSchema = z
  .object({
    applicantDateOfBirth: z.date(),
    spouseDateOfBirth: z.date(),
    applicantSSN: SSNSchema,
    spouseSSN: z.string().refine((val) => !val || isValidSSN(val), {
      message: 'invalid_ssn',
    }),
    filingType: FilingStatusSchema.or(z.null()),
  })
  .superRefine((data, ctx) => {
    if (data.filingType === 'individually' && !data.spouseDateOfBirth) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Spouse SSN not required when filing jointly',
      })
    }
    if (data.filingType === 'individually' && !data.spouseSSN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Spouse SSN required when filing individually',
        path: ['spouseSSN'],
      })
    }
  })

const FORM_ID = 'dws-income-verification'

export type SSNFormSchema = z.infer<typeof ssnFormSchema>

type Props = {
  onDwsFailed: () => void
  onSkipClick: () => void
  onNotRecievingBenefitContinueClick: () => void
  onRecievingBenfitContinueClick: () => void
}

export default function DWSIncomePage(props: Props) {
  const {
    onNotRecievingBenefitContinueClick,
    onSkipClick,
    onRecievingBenfitContinueClick,
    onDwsFailed
  } = props

  const { familyApplication } = useOutletContext<FamilyApplicationData>()

  const { t: translate, i18n } = useTranslation()
  const { fireAlert } = useAppAlert()

  const [displayModal, setDisplayModal] = useState(false)
  const [open, setIsOpen] = useState(false)
  const [applicantDobOpen, setApplicantDobOpen] = useState(false)
  const programId = useSelector(selectProgramID)
  const {
    request: { status },
  } = useSelector((state: RootState) => state.FamilyApplication)
  const dispatch = useDispatch<AppDispatch>()

  const isFilingSeparately =
    familyApplication.household_filed_federal_income === 'individually'

  const {
    mutate,
    status: dwsStatus,
    data,
  } = useMutation({
    mutationFn: async (spouseDob: string) =>
      await CheckDWSIncome({
        include_spouse: isFilingSeparately,
        spouse_dob: spouseDob,
        program_id: programId,
        application_id: familyApplication.application_id,
      }),
    onError: () => {
      fireAlert({
        variant: 'error',
        message: translate("temp_dws_error"),
      }).then(() => {
        onDwsFailed()
      })
    },
  })


  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current && (dwsStatus == "error" || (data && !data.receivingBenefit))) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'the DWS failed page',
      })
      hasLoggedPageView.current = true;
    }
  }, [dwsStatus, data]) 

  const language = i18n.language
  const isRenewal = isRenewalApplicant(familyApplication)

  const {
    handleSubmit,
    setValue,
    getValues,
    watch,
    control,
    formState: { errors },
  } = useForm<SSNFormSchema>({
    mode: 'onBlur',
    resolver: zodResolver(ssnFormSchema),
    defaultValues: {
      applicantSSN: familyApplication.SSN
        ? `${MASKED_SSN_STRUCTURE}${familyApplication.SSN}`
        : '',
      spouseSSN: familyApplication.spouse_ssn
        ? `${MASKED_SSN_STRUCTURE}${familyApplication.spouse_ssn}`
        : '',
      spouseDateOfBirth: new Date('1970-01-01'),
      applicantDateOfBirth: familyApplication.date_of_birth
        ? parse(familyApplication.date_of_birth, 'yyyy-MM-dd', new Date())
        : new Date(),
      filingType: familyApplication.household_filed_federal_income,
    },
  })

  function getLocale(string: string): Locale {
    if (string === 'es') {
      return es
    }

    return enUS
  }

  async function onSubmit(data: SSNFormSchema) {
    const spouseData = isFilingSeparately ? data.spouseSSN : undefined

    if (
      isMaskedSSN(data.applicantSSN) &&
      spouseData &&
      isMaskedSSN(spouseData)
    ) {
      const dateToYYYYMMDD = format(data.spouseDateOfBirth, 'yyyy-MM-dd')
      mutate(dateToYYYYMMDD)
      return
    }
    const applicantDob = format(data.applicantDateOfBirth, 'yyyy-MM-dd')

    const updateApplicantResponse = await dispatch(
      UpdateApplicant({
        program_id: programId,
        SSN: isMaskedSSN(data.applicantSSN) ? undefined : data.applicantSSN,
        spouse_ssn: isMaskedSSN(data.spouseSSN) ? undefined : spouseData,
        date_of_birth: isRenewal ? applicantDob : undefined,
      })
    )
    if (UpdateApplicant.fulfilled.match(updateApplicantResponse)) {
      const dateToYYYYMMDD = format(data.spouseDateOfBirth, 'yyyy-MM-dd')
      mutate(dateToYYYYMMDD)
    } else {
      const SSN_AREADY_EXISTS_ERROR_CODE = 71
      const isSSNError =
        updateApplicantResponse?.payload?.code === SSN_AREADY_EXISTS_ERROR_CODE

      if (isSSNError) {
        fireAlert({
          variant: 'error',
          message: translate('ssn_already_exists'),
          confirmBtn: true,
          title: true,
          titleText: translate('duplicate_ssn'),
          confirmBtnText: translate('contact_support'),
          denyBtn: true,
          denyBtnText: `${translate('back')}`,
        }).then((result) => {
          if (result.confirmed) {
            setDisplayModal(true)
          }
        })
      } else {
        fireAlert({
          variant: 'error',
          message:
            updateApplicantResponse?.payload?.message ??
            static_errors.unexpected_contact_support,
        })
      }
    }
  }

  if (dwsStatus === 'success' && !data.receivingBenefit) {
    return (
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          {translate('unable_to_verify_dws_text')}
        </FamilyWizardLayout.Header>

        <FamilyWizardLayout.Content className="mt-10">
          <AppAlertComponent
            title={translate('not_found')}
            variant="error"
            text={translate('unable_to_verify_dws')}
          >
            <Link
              className={buttonVariants({
                variant: 'primary',
                className: 'w-full',
              })}
              to={
                AppRoutePaths.family_application.income_verification
                  .alternative_verification_method
              }
              onClick={() => {
                onNotRecievingBenefitContinueClick()
              }}
            >
              {translate('continue')}
            </Link>
          </AppAlertComponent>
        </FamilyWizardLayout.Content>
      </FamilyWizardLayout>
    )
  }

  if (dwsStatus === 'success') {
    return (
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          {translate('success')}
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content>
          <AppAlertComponent
            title={translate('dws_verification_complete')}
            variant="success"
            text={translate('dws_qualifies_you_for_tier_2')}
          >
            <Button className="w-full" onClick={async () => {
              await dispatch(UpdateIncomeDetails({
                program_id: programId,
                skip_income_verification: false
              }))
              onRecievingBenfitContinueClick()
            }}>
              {translate('continue')}
            </Button>
          </AppAlertComponent>
        </FamilyWizardLayout.Content>
      </FamilyWizardLayout>
    )
  }

  if (status === 'in_flight' || dwsStatus === 'pending') {
    return (
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          {translate('dws_workforce_long')}
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content>
          <LoadingCard isLoading={true}>{translate('processing')}</LoadingCard>
        </FamilyWizardLayout.Content>
      </FamilyWizardLayout>
    )
  }

  watch(['applicantSSN', 'spouseSSN', 'applicantDateOfBirth'])

  return (
    <>
      <ContactSupportModal
        trigger={<></>}
        isDialogOpen={displayModal}
        setIsDialogOpen={setDisplayModal}
      />
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          <h1>{translate('social_security_number')}</h1>
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content>
          <section id="income-verification">
            <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
              <div className="mt-6">
                {isV1RenewalApplication(familyApplication) && (
                  <div className="mt-6 mb-6">
                    <Label className="mb-2 block">
                      {translate('applicant')} {translate('date_of_birth')}
                    </Label>
                    <Controller
                      control={control}
                      name="applicantDateOfBirth"
                      render={({ field }) => (
                        <DatePicker
                          fromDate={new Date('1920-01-01')}
                          toDate={new Date()}
                          captionLayout="dropdown"
                          open={applicantDobOpen}
                          onOpenChange={setApplicantDobOpen}
                          locale={getLocale(language)}
                          className="w-full"
                          selected={new Date(field.value)}
                          defaultMonth={new Date(field.value)}
                          mode="single"
                          onSelect={(date) => {

                            field.onChange(date)
                            if (date) {
                              setApplicantDobOpen(false)
                            }
                          }}
                        />
                      )}
                    />
                  </div>
                )}
                <FormErrorMessage
                  message={errors.applicantDateOfBirth?.message}
                />
                <div className="mb-6">
                  <SSNInput
                    value={getValues().applicantSSN}
                    onChange={(val) => setValue('applicantSSN', val)}
                    error={errors.applicantSSN?.message}
                    label={translate('social_security_number')}
                  />
                  <p className="text-gray-600 text-xs mt-2">
                    {translate('ssn_applicant_desc')}
                  </p>
                </div>
                {isFilingSeparately && (
                  <div>
                    <SSNInput
                      value={getValues().spouseSSN ?? ''}
                      onChange={(val) => setValue('spouseSSN', val)}
                      error={errors.spouseSSN?.message}
                      label={`${translate("Spouse's")} ${translate('social_security_number')}`}
                    />

                    <p className="text-gray-600 text-xs mt-2">
                      {translate('ssn_spouse_desc')}
                    </p>

                    <div className="mt-6">
                      <Label className="mb-4 block">
                        {translate("Spouse's")} {translate('date_of_birth')}
                      </Label>
                      <Controller
                        control={control}
                        name="spouseDateOfBirth"
                        render={({ field }) => (
                          <DatePicker
                            fromDate={new Date('1920-01-01')}
                            toDate={new Date()}
                            captionLayout="dropdown"
                            open={open}
                            onOpenChange={setIsOpen}
                            locale={getLocale(language)}
                            className="w-full"
                            selected={new Date(field.value)}
                            defaultMonth={new Date(field.value)}
                            mode="single"
                            onSelect={(date) => {
                              field.onChange(date)
                              if (date) {
                                setIsOpen(false)
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                    <FormErrorMessage
                      message={errors.spouseDateOfBirth?.message}
                    />
                  </div>
                )}
              </div>
            </form>
          </section>
        </FamilyWizardLayout.Content>
        <FamilyWizardLayout.ActionBar className="flex flex-col gap-2">
          <SubmitButton
            buttonTxt={translate('continue')}
            form={FORM_ID}
            status={status}
          />
          <Button
            className="h-10"
            onClick={onSkipClick}
            variant={'text'}
            size={'text'}
            type="button"
          >
            {translate('dont_want_to_provide')}
          </Button>
        </FamilyWizardLayout.ActionBar>
      </FamilyWizardLayout>
    </>
  )
} 
